import { useState, useRef, useEffect, useCallback, useMemo, memo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageBubble } from "./message-bubble";
import { ChatInput } from "./chat-input";
import { ModelSelector } from "./model-selector";
import { LanguageSelector } from "./language-selector";
import { Button } from "@/components/ui/button";
import { Trash2, ExternalLink, ChevronUp, ChevronDown, ArrowUp, ArrowDown, Download } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import CSATFeedback, { CSATFeedbackHandle } from "@/components/feedback/csat-feedback";
import { FontSizeControls } from "@/components/ui/font-size-controls";
import { ErrorPage } from "@/components/ui/error-page";
import { useHapticFeedback } from "@/hooks/use-haptic";
import { useFeedbackTracker } from "@/hooks/use-feedback-tracker";
// DevToolsButton removed
import i18n from "@/lib/i18n";
import { Trans } from "react-i18next";
import { dynamicActivate } from "@/lib/i18n";
import { useTranslation } from "@/lib/translation-utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLanguage } from "@/lib/language-context";
import {
  ApiResponse,
  Message,
  ModelType,
  ModelMessages,
  LocaleType,
  createWelcomeMessage,
  createDisclaimerMessage,
  errorMessages
} from "@/types/chat";
import { FrameworkSelector } from "./framework-selector";
import type { Framework } from "@/types/framework";

// Memoized child components for better performance
const MemoizedMessageBubble = memo(MessageBubble);
const MemoizedChatInput = memo(ChatInput);
const MemoizedModelSelector = memo(ModelSelector);
const MemoizedFontSizeControls = memo(FontSizeControls);

// Add new error types
const SSE_ERROR_TYPES = {
  CONNECTION: 'connection_error',
  TIMEOUT: 'timeout_error',
  DATA: 'data_error',
  NETWORK: 'network_error'
} as const;

// Enhanced LoadingDots component with stability improvements
const LoadingDots = memo(() => {
  // Using a ref ensures the component doesn't unnecessarily rerender
  const dotsRef = useRef(null);
  
  return (
    <div 
      className="loading-dots" 
      role="status" 
      aria-label="Loading"
      ref={dotsRef}
      key="loading-dots-stable-key"
    >
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
    </div>
  );
});

const MessageBubbleWrapper = ({ message, model }: { message: Message, model: ModelType }) => {
  return (
    <MemoizedMessageBubble
      message={message}
      isLoading={!message.content && message.isLoading}
      model={model}
      isPartial={Boolean(message.content) && message.isPartial}
    >
      {!message.content && message.isLoading && <LoadingDots />}
    </MemoizedMessageBubble>
  );
};

// Main component
export const ChatInterface: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { triggerHaptic } = useHapticFeedback();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout>();
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const activeEventSourceRef = useRef<EventSource | null>(null);
  const csatFeedbackRef = useRef<CSATFeedbackHandle>(null);
  const { t } = useTranslation(); // Get translation function
  
  // Track AI responses and feedback status
  const [aiResponseCount, setAiResponseCount] = useState<number>(0);
  const [hasFeedbackBeenSubmitted, setHasFeedbackBeenSubmitted] = useState<boolean>(false);

  // Optimized state management with proper initialization
  const [modelMessages, setModelMessages] = useState<ModelMessages>(() => {
    try {
      // Check for existing messages in localStorage
      const storedMessages = localStorage.getItem('chatMessages');
      
      if (storedMessages) {
        const parsedMessages = JSON.parse(storedMessages);
        
        // Check if welcome and disclaimer messages need to be updated for current language
        const currentLang = i18n.language;
        
        // Create a copy of the parsed messages to avoid mutating original
        const updatedMessages = { ...parsedMessages };
        let needsLanguageUpdate = false;
        
        // Check each model's welcome and disclaimer messages
        Object.keys(updatedMessages).forEach(model => {
          const modelKey = model as ModelType;
          if (updatedMessages[modelKey].length >= 2) {
            const welcomeIndex = updatedMessages[modelKey].findIndex((msg: Message) => msg.id === 'welcome');
            const disclaimerIndex = updatedMessages[modelKey].findIndex((msg: Message) => msg.id === 'disclaimer');
            
            // Replace welcome and disclaimer with current language versions if needed
            if (welcomeIndex !== -1) {
              updatedMessages[modelKey][welcomeIndex] = createWelcomeMessage(modelKey);
              needsLanguageUpdate = true;
            }
            
            if (disclaimerIndex !== -1) {
              updatedMessages[modelKey][disclaimerIndex] = createDisclaimerMessage();
              needsLanguageUpdate = true;
            }
          }
        });
        
        // If language updates were needed, return updated messages
        if (needsLanguageUpdate) {
          return updatedMessages;
        }
        
        // Otherwise return original messages
        return parsedMessages;
      }
      
      // If no stored messages, create new ones
      return {
        claude: [createWelcomeMessage("claude"), createDisclaimerMessage()],
        deepseek: [createWelcomeMessage("deepseek"), createDisclaimerMessage()],
        mjlab: [createWelcomeMessage("mjlab"), createDisclaimerMessage()],
      };
    } catch (e) {
      console.warn('[Chat] Failed to parse stored messages:', e);
      return {
        claude: [createWelcomeMessage("claude"), createDisclaimerMessage()],
        deepseek: [createWelcomeMessage("deepseek"), createDisclaimerMessage()],
        mjlab: [createWelcomeMessage("mjlab"), createDisclaimerMessage()],
      };
    }
  });

  const [selectedModel, setSelectedModel] = useState<ModelType>(() => {
    const storedModel = localStorage.getItem('selectedModel');
    return (storedModel === 'claude' || storedModel === 'deepseek' || storedModel === 'mjlab')
      ? storedModel
      : "claude";
  });

  // Rest of the states with proper typing
  const [testError, setTestError] = useState<{ code: number; message: string } | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [isLogoRotating, setIsLogoRotating] = useState(false);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [showSushiSplash, setShowSushiSplash] = useState(false);
  const [hasSushiBeenShown, setHasSushiBeenShown] = useState(() => {
    try {
      const stored = localStorage.getItem('hasSushiBeenShown');
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });
  const [showMjLab, setShowMjLab] = useState(() => {
    try {
      const stored = localStorage.getItem('showMjLab');
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });
  const [selectedFramework, setSelectedFramework] = useState<Framework>(() => {
    const storedFramework = localStorage.getItem('selectedFramework');
    return (storedFramework as Framework) || "AUTO";
  });
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected'>('disconnected');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Use language context instead of local state
  const { locale: selectedLocale, setLocale: handleLocaleChange } = useLanguage();
  
  // Effect to ensure welcome/disclaimer messages match the current language
  useEffect(() => {
    // If we have i18n and a selectedLocale, ensure welcome messages are in the right language
    // Silently update welcome and disclaimer messages to match the current language
    setModelMessages(prev => {
      const updatedMessages: ModelMessages = {
        claude: [...prev.claude],
        deepseek: [...prev.deepseek],
        mjlab: [...prev.mjlab]
      };
      
      let changesNeeded = false;
      
      // Check each model's messages
      Object.keys(updatedMessages).forEach(model => {
        const modelKey = model as ModelType;
        if (updatedMessages[modelKey].length >= 2) {
          // Find the welcome and disclaimer messages by their IDs
          const welcomeIndex = updatedMessages[modelKey].findIndex((msg: Message) => msg.id === 'welcome');
          const disclaimerIndex = updatedMessages[modelKey].findIndex((msg: Message) => msg.id === 'disclaimer');
          
          // Replace welcome message if found
          if (welcomeIndex !== -1) {
            const oldWelcome = updatedMessages[modelKey][welcomeIndex];
            const newWelcome = createWelcomeMessage(modelKey);
            if (oldWelcome.content !== newWelcome.content) {
              updatedMessages[modelKey][welcomeIndex] = newWelcome;
              changesNeeded = true;
            }
          }
          
          // Replace disclaimer message if found
          if (disclaimerIndex !== -1) {
            const oldDisclaimer = updatedMessages[modelKey][disclaimerIndex];
            const newDisclaimer = createDisclaimerMessage();
            if (oldDisclaimer.content !== newDisclaimer.content) {
              updatedMessages[modelKey][disclaimerIndex] = newDisclaimer;
              changesNeeded = true;
            }
          }
        }
      });
      
      // Only return new reference if changes were needed
      return changesNeeded ? updatedMessages : prev;
    });
  }, [selectedLocale]); // Re-run when language changes

  // Effect to handle auto-open of feedback form after 3rd AI response
  useEffect(() => {
    if (aiResponseCount === 3 && !hasFeedbackBeenSubmitted && csatFeedbackRef.current) {
      // Open the feedback form after a short delay
      const timer = setTimeout(() => {
        csatFeedbackRef.current?.openDialog();
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [aiResponseCount, hasFeedbackBeenSubmitted]);
  
  // Function to handle the completion of an AI response
  const handleAIResponseComplete = useCallback(() => {
    setAiResponseCount(prev => prev + 1);
  }, []);
  
  // Function to mark feedback as submitted
  const handleFeedbackSubmitted = useCallback(() => {
    setHasFeedbackBeenSubmitted(true);
  }, []);

  // Synchronous cleanup function
  const cleanupEventSource = () => {
    if (activeEventSourceRef.current) {
      console.log("[Chat Client] Cleaning up existing EventSource connection");
      try {
        // Remove all listeners first
        const events = activeEventSourceRef.current;
        events.removeEventListener('open', () => {});
        events.removeEventListener('error', () => {});
        events.removeEventListener('message', () => {});
        events.removeEventListener('chunk', () => {});
        events.removeEventListener('heartbeat', () => {});

        // Close the connection
        events.close();
        console.log("[Chat Client] EventSource connection closed");
      } catch (error) {
        console.error("[Chat Client] Error during EventSource cleanup:", error);
      } finally {
        activeEventSourceRef.current = null;
        setConnectionState('disconnected');
      }
    }
  };

  // Optimized message handling mutation
  const { mutate: sendMessage, isPending } = useMutation({
    mutationFn: async (content: string): Promise<void> => {
      // Synchronously cleanup any existing connection
      cleanupEventSource();

      // Handle MJ Lab unlock/lock commands
      if (content.toLowerCase() === "unlock mj lab") {
        setShowMjLab(true);
        localStorage.setItem('showMjLab', 'true');

        // Add the command message to chat
        setModelMessages(prev => ({
          ...prev,
          [selectedModel]: [...prev[selectedModel], {
            id: Date.now().toString(),
            role: "assistant",
            content: "🔓 MJ Lab mode has been unlocked! You can now select MJ Lab from the model selector.",
            timestamp: Date.now()
          }]
        }));
        return;
      }

      if (content.toLowerCase() === "lock mj lab") {
        setShowMjLab(false);
        localStorage.setItem('showMjLab', 'false');
        // If MJ Lab is currently selected, switch to Claude
        if (selectedModel === 'mjlab') {
          setSelectedModel('claude');
          localStorage.setItem('selectedModel', 'claude');
        }

        // Add the command message to chat
        setModelMessages(prev => ({
          ...prev,
          [selectedModel]: [...prev[selectedModel], {
            id: Date.now().toString(),
            role: "assistant",
            content: "🔒 MJ Lab mode has been locked and disabled.",
            timestamp: Date.now()
          }]
        }));
        return;
      }

      //Check if user wants a summary
      if (selectedModel === 'mjlab') {
        // Mock response with delay to simulate processing
        await new Promise(resolve => setTimeout(resolve, 500));

        // Check if user wants a summary
        const isSummaryRequest = content.toLowerCase().includes('summarize') ||
          content.toLowerCase().includes('summary');

        // Mock response for MJ Lab with proper typing
        const mockResponse: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: isSummaryRequest ?
            // Generate a concise summary following the 50-word limit
            "In alignment with NCA requirements, this framework establishes crucial cybersecurity controls, risk assessment protocols, and implementation guidelines. It emphasizes regular security monitoring, incident response, and compliance verification for Saudi organizations." :
            `I understand you said: "${content}"\n\nThis is the MJ Lab test environment. I'm currently in development, but I'm learning to assist with NCA regulations just like Claude and DeepSeek. How can I help you further with your compliance needs?`,
          timestamp: Date.now(),
          framework: selectedFramework === "AUTO" ? undefined : selectedFramework,
        };

        // Add the mock response to chat messages with proper typing
        setModelMessages(prev => ({
          ...prev,
          [selectedModel]: [...prev[selectedModel], mockResponse]
        }));
        
        // Increment AI response count for MJ Lab responses too
        handleAIResponseComplete();

        return;
      }

      const apiMessages = modelMessages[selectedModel]
        // Exclude UI-only scaffolding (welcome greeting + legal disclaimer) from the
        // conversation sent to the model. These are rendered in the interface language,
        // so including them biased the model into replying in the UI language instead of
        // the language the user actually typed in.
        .filter((msg) => msg.id !== 'welcome' && msg.id !== 'disclaimer')
        .map(({ role, content }) => ({
          role,
          content
        }));

      console.log('Sending chat request with framework:', selectedFramework);

      // Initialize chat session
      const initResponse = await fetch("/api/chat/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [...apiMessages, {
            role: "user",
            content,
            framework: selectedFramework
          }],
          model: selectedModel,
          framework: selectedFramework,
        }),
      });

      if (!initResponse.ok) {
        const errorData = await initResponse.json();
        throw new Error(errorData.error || `Initialization failed: ${initResponse.status}`);
      }

      setConnectionState('connecting');
      const events = new EventSource('/api/chat/stream', {
        withCredentials: true
      });
      activeEventSourceRef.current = events;
      console.log("[Chat Client] New EventSource connection created");

      return new Promise((resolve, reject) => {
        const tempMessageId = Date.now().toString();
        let isCleanupInitiated = false;

        // Create initial loading message - only once
        setModelMessages(prev => ({
          ...prev,
          [selectedModel]: [...prev[selectedModel], {
            id: tempMessageId,
            role: "assistant",
            content: "",
            timestamp: Date.now(),
            isLoading: true,
            isPartial: false,
            model: selectedModel
          }]
        }));


        const HEARTBEAT_INTERVAL = 30000; // 30s
        const CONNECTION_TIMEOUT = 60000; // 60s

        const cleanup = () => {
          if (isCleanupInitiated) return;
          isCleanupInitiated = true;

          if (heartbeatRef.current) {
            clearTimeout(heartbeatRef.current);
            heartbeatRef.current = null;
          }

          cleanupEventSource(); // Use the synchronous cleanup
          resolve();
        };

        const handleHeartbeat = (e?: MessageEvent) => {
          console.debug('[Chat Client] Heartbeat received');

          // Clear existing timeout
          if (heartbeatRef.current) {
            clearTimeout(heartbeatRef.current);
          }

          // Set new timeout
          heartbeatRef.current = setTimeout(() => {
            console.warn('[Chat Client] Heartbeat timeout');
            setConnectionState('disconnected');
            cleanup();
            reject(new Error('Connection timeout - no heartbeat received'));
          }, CONNECTION_TIMEOUT);
        };

        const handleOpen = () => {
          console.log("[Chat Client] SSE Connection established");
          setConnectionState('connected');
          handleHeartbeat(); // Initialize heartbeat timer
        };

        // Handle incoming chunks from SSE
        const handleChunk = (e: MessageEvent) => {
          try {
            const data = JSON.parse(e.data);

            setModelMessages(prev => {
              const updatedMessages = [...prev[selectedModel]];
              const messageIndex = updatedMessages.findIndex((msg: Message) => msg.id === tempMessageId);

              if (messageIndex !== -1) {
                // Update existing message
                updatedMessages[messageIndex] = {
                  ...updatedMessages[messageIndex],
                  content: data.isPartial ? updatedMessages[messageIndex].content + data.content : data.content,
                  isPartial: data.isPartial,
                  isLoading: false,
                  timestamp: Date.now()
                };
              }

              return {
                ...prev,
                [selectedModel]: updatedMessages
              };
            });

            // Smooth scroll with each update
            requestAnimationFrame(() => {
              if (chatContainerRef.current) {
                chatContainerRef.current.scrollTo({
                  top: chatContainerRef.current.scrollHeight,
                  behavior: 'smooth'
                });
              }
            });
          } catch (error) {
            console.error("[Chat Client] Chunk processing error:", error);
            cleanup();
            reject(new Error('Failed to process message chunk'));
          }
        };

        // Handle final message
        const handleMessage = (e: MessageEvent) => {
          try {
            const data = JSON.parse(e.data);

            if (data.type === 'heartbeat') {
              handleHeartbeat(e);
              return;
            }

            // Immediately set the complete message for both models
            setModelMessages(prev => {
              const updatedMessages = prev[selectedModel].filter(msg => msg.id !== tempMessageId);
              return {
                ...prev,
                [selectedModel]: [
                  ...updatedMessages,
                  {
                    ...data.message,
                    timestamp: Date.now(),
                    isPartial: false,
                    model: selectedModel,
                    content: selectedModel === 'deepseek' ? data.message.content :
                      prev[selectedModel].find((msg: Message) => msg.id === tempMessageId)?.content || data.message.content
                  }
                ]
              };
            });

            // Increment AI response count after message is complete
            handleAIResponseComplete();
            
            cleanup();
            resolve();
          } catch (error) {
            console.error("[Chat Client] Message processing error:", error);
            cleanup();
            reject(error);
          }
        };

        const handleError = (e: Event) => {
          const errorDetails = {
            type: SSE_ERROR_TYPES.CONNECTION,
            timestamp: new Date().toISOString(),
            connectionState,
            readyState: events.readyState,
            error: e instanceof ErrorEvent ? e.message : 'Connection closed'
          };

          console.error("[Chat Client] SSE Error:", errorDetails);
          cleanup();
          reject(new Error(`SSE Error: ${errorDetails.error}`));
        };

        // Set up event listeners
        events.addEventListener('open', handleOpen);
        events.addEventListener('error', handleError);
        events.addEventListener('message', handleMessage);
        events.addEventListener('chunk', handleChunk);
        events.addEventListener('heartbeat', handleHeartbeat);

        // Cleanup on component unmount
        return cleanup;
      });
    },
    onError: (error: Error) => {
      console.error('[Chat Client] Error:', error);
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
        duration: 5000,
      });
    }
  });

  // Memoized scroll animation ID to avoid duplicate animations
  const scrollAnimationIdRef = useRef<number | null>(null);
  
  // Enhanced scroll handler with animation frame deduplication
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (!chatContainerRef.current) return;
    
    // Cancel any pending scroll animation to prevent stacking animations
    if (scrollAnimationIdRef.current !== null) {
      cancelAnimationFrame(scrollAnimationIdRef.current);
      scrollAnimationIdRef.current = null;
    }
    
    // Use a single requestAnimationFrame for all scroll operations
    scrollAnimationIdRef.current = requestAnimationFrame(() => {
      if (!chatContainerRef.current) return;
      
      const scrollHeight = chatContainerRef.current.scrollHeight;
      const currentScroll = chatContainerRef.current.scrollTop;
      const clientHeight = chatContainerRef.current.clientHeight;
      
      // Only scroll if we're not already near the bottom (within 10px)
      const isNearBottom = currentScroll + clientHeight >= scrollHeight - 10;
      
      if (!isNearBottom) {
        chatContainerRef.current.scrollTo({
          top: scrollHeight,
          behavior: behavior === 'smooth' ? 'smooth' : 'auto'
        });
      }
      
      scrollAnimationIdRef.current = null;
    });
    
    // Cleanup function for component unmount
    return () => {
      if (scrollAnimationIdRef.current !== null) {
        cancelAnimationFrame(scrollAnimationIdRef.current);
        scrollAnimationIdRef.current = null;
      }
    };
  }, []);

  // Optimized effects with proper cleanup and dependencies
  useEffect(() => {
    if (!chatContainerRef.current) return;

    chatContainerRef.current.scrollTo({ top: 0 });
    const timeoutId = setTimeout(() => scrollToBottom('smooth'), 800);

    return () => clearTimeout(timeoutId);
  }, [scrollToBottom]);


  // Memoized effect for handling message updates and scrolling
  useEffect(() => {
    const messages = modelMessages[selectedModel];
    if (messages.length === 0) return;
    
    const lastMessage = messages[messages.length - 1];
    
    // Only scroll when a full message from the assistant is received
    if (lastMessage.role === 'assistant' && !lastMessage.isPartial) {
      // Use a short delay to ensure content is fully rendered
      const scrollTimeout = setTimeout(() => {
        if (chatContainerRef.current) {
          // Direct call to scrollToBottom which already handles requestAnimationFrame
          scrollToBottom('auto');
        }
      }, 50);
      
      return () => clearTimeout(scrollTimeout);
    }
  }, [modelMessages, selectedModel, scrollToBottom]);

  // Optimized effect for handling scrolling during pending state
  useEffect(() => {
    // Only scroll when a message is being processed (isPending is true)
    if (!isPending || !chatContainerRef.current) return;
    
    // Use our optimized scrollToBottom function which handles requestAnimationFrame internally
    scrollToBottom('smooth');
  }, [isPending, scrollToBottom]);

  useEffect(() => {
    try {
      // Use a debounced approach to avoid excessive writes
      const saveTimeout = setTimeout(() => {
        try {
          // First check available storage before attempting to save
          if ('storage' in navigator && 'estimate' in navigator.storage) {
            navigator.storage.estimate().then(({usage, quota}) => {
              // If we're using more than 80% of available storage, trim early
              if (usage && quota && (usage / quota > 0.8)) {
                console.warn('[Chat] Storage usage high (>80%), pre-emptively trimming messages');
                saveWithTrimming();
                return;
              }
              
              // Otherwise try normal save
              saveFull();
            }).catch(err => {
              // Fall back to normal save if estimate fails
              console.warn('[Chat] Storage estimate failed:', err);
              saveFull();
            });
          } else {
            // No storage API, try normal save
            saveFull();
          }
        } catch (error) {
          console.error('[Chat] Storage management error:', error);
          // Still try basic save in case of unexpected error
          saveFull();
        }
      }, 500); // 500ms debounce
      
      const saveFull = () => {
        try {
          // Check the size before trying to save
          const serialized = JSON.stringify(modelMessages);
          const size = new Blob([serialized]).size;
          const MAX_SIZE = 4.5 * 1024 * 1024; // 4.5MB to stay under 5MB quota
          
          if (size > MAX_SIZE) {
            console.warn(`[Chat] Message size (${Math.round(size/1024)}KB) exceeds limit (${Math.round(MAX_SIZE/1024)}KB)`);
            saveWithTrimming();
            return;
          }
          
          localStorage.setItem('chatMessages', serialized);
          // Silent operation - no logging for routine actions
        } catch (error) {
          console.error('[Chat] Failed to save messages to localStorage:', error);
          saveWithTrimming();
        }
      };
      
      const saveWithTrimming = () => {
        try {
          // First try: Save only the current model's full conversation
          const currentModelOnly = {
            [selectedModel]: modelMessages[selectedModel]
          } as ModelMessages;
          
          try {
            const serialized = JSON.stringify(currentModelOnly);
            const size = new Blob([serialized]).size;
            
            if (size < 2 * 1024 * 1024) { // Less than 2MB
              localStorage.setItem('chatMessages', serialized);
              console.warn('[Chat] Saved current model messages only');
              
              toast({
                title: t("Storage optimization"),
                description: t("Only current model messages are being saved."),
                duration: 5000
              });
              return;
            }
          } catch (serializeError) {
            console.warn('[Chat] Current model serialization failed:', serializeError);
          }
          
          // Second try: Save trimmed messages for all models
          const trimmedMessages: ModelMessages = Object.keys(modelMessages).reduce((acc, modelKey) => {
            const model = modelKey as ModelType;
            const messages = modelMessages[model];
            // Keep welcome, disclaimer, and the last 8 messages (reduced from 18)
            const welcomeAndDisclaimer = messages.slice(0, 2);
            const recentMessages = messages.slice(-8);
            acc[model] = [...welcomeAndDisclaimer, ...recentMessages];
            return acc;
          }, {} as ModelMessages);
          
          try {
            // Try stringifying first before saving to catch JSON errors
            const jsonString = JSON.stringify(trimmedMessages);
            localStorage.setItem('chatMessages', jsonString);
            console.warn('[Chat] Saved trimmed messages due to quota limits');
            
            toast({
              title: t("Storage limit reached"),
              description: t("Some older messages were removed to save space."),
              duration: 5000
            });
          } catch (stringifyError) {
            console.error('[Chat] JSON stringify error:', stringifyError);
            
            // Last resort: Just save minimal history for current model to session storage
            try {
              const minimal = {
                [selectedModel]: [
                  ...modelMessages[selectedModel].slice(0, 2), // Welcome and disclaimer
                  ...modelMessages[selectedModel].slice(-5)    // Last 5 messages
                ]
              };
              sessionStorage.setItem('chatMessages_fallback', JSON.stringify(minimal));
              console.log('[Chat] Used session storage fallback');
              
              toast({
                title: t("Using temporary storage"),
                description: t("Chat history will be available until you close the browser."),
                duration: 7000
              });
            } catch (finalError) {
              console.error('[Chat] All storage strategies failed');
              // App continues without persistence
            }
          }
        } catch (innerError) {
          console.error('[Chat] Failed to save even trimmed messages:', innerError);
          // App can continue functioning without persistence
        }
      };
      
      return () => clearTimeout(saveTimeout);
    } catch (outerError) {
      // Catch any other unexpected errors
      console.error('[Chat] Unexpected error in storage effect:', outerError);
    }
  }, [modelMessages, toast]);

  // Create a throttled resize handler for iOS devices
  useEffect(() => {
    let throttleTimeout: number | undefined;
    let lastTime = 0;
    
    const throttledHandleResize = () => {
      const currentTime = Date.now();
      const timeSinceLastCall = currentTime - lastTime;
      const throttleDelay = 100; // 100ms throttle
      
      // Only run if we're on iOS devices
      if (!/iPad|iPhone|iPod/.test(navigator.userAgent)) return;
      
      // Clear existing timeout
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
        throttleTimeout = undefined;
      }
      
      // If enough time has passed, execute immediately
      if (timeSinceLastCall >= throttleDelay) {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty("--vh", `${vh}px`);
        lastTime = currentTime;
      } else {
        // Otherwise, schedule to run after delay
        throttleTimeout = window.setTimeout(() => {
          const vh = window.innerHeight * 0.01;
          document.documentElement.style.setProperty("--vh", `${vh}px`);
          lastTime = Date.now();
          throttleTimeout = undefined;
        }, throttleDelay - timeSinceLastCall);
      }
    };
    
    // Initial call
    throttledHandleResize();
    
    // Add event listener
    window.addEventListener("resize", throttledHandleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener("resize", throttledHandleResize);
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, []);

  // Keep the existing timer effect but fix the initialization
  useEffect(() => {
    if (!isPending) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
      setElapsedTime(0);
      return;
    }

    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
    };
  }, [isPending]);

  // Event handlers
  const handleSend = useCallback((content: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content,
      timestamp: Date.now(),
      framework: selectedFramework === "AUTO" ? undefined : selectedFramework,
    };

    setModelMessages((prev) => ({
      ...prev,
      [selectedModel]: [...prev[selectedModel], newMessage],
    }));

    setUserMessageCount((prev) => {
      const newCount = prev + 1;
      if (newCount === 3 && !hasSushiBeenShown) {
        setShowSushiSplash(true);
        setHasSushiBeenShown(true);
        
        // Try to save sushi display state to localStorage, but handle errors
        try {
          localStorage.setItem('hasSushiBeenShown', 'true');
        } catch (error) {
          console.warn('[Chat] Failed to save sushi state to localStorage:', error);
          // Continue regardless of localStorage error
        }
        
        setTimeout(() => setShowSushiSplash(false), 3000);
      }
      return newCount;
    });

    // Use the optimized scrollToBottom function which already handles requestAnimationFrame
    scrollToBottom();

    // Send message to backend via mutation
    sendMessage(content);
  }, [selectedModel, selectedFramework, hasSushiBeenShown, sendMessage, scrollToBottom]);

  const handleClear = async () => {
    try {
      // Check if there are messages to clear (more than the welcome and disclaimer messages)
      const hasMessagesToClear = currentMessages.length > 2;

      if (!hasMessagesToClear) {
        toast({
          description: t("Nothing to clear"),
          duration: 2000
        });
        return;
      }

      try {
        await triggerHaptic("warning");
      } catch (hapticError) {
        // Non-critical error, can proceed without haptic feedback
        console.warn("[Chat] Haptic feedback failed:", hapticError);
      }
      
      // Reset messages to initial state
      setModelMessages((prev) => ({
        ...prev,
        [selectedModel]: [createWelcomeMessage(selectedModel), createDisclaimerMessage()],
      }));
      
      // Reset framework to AUTO
      setSelectedFramework("AUTO");
      
      // Reset other state
      setUserMessageCount(0);
      setHasSushiBeenShown(false);
      setShowSushiSplash(false);
      
      // Try to update localStorage but handle failures gracefully
      try {
        localStorage.setItem('selectedFramework', 'AUTO');
        localStorage.removeItem('chatMessages');
        localStorage.removeItem('hasSushiBeenShown');
        console.log('[Chat] Successfully cleared chat data from localStorage');
      } catch (storageError) {
        console.warn('[Chat] Failed to clear data from localStorage:', storageError);
        // Continue regardless of localStorage error - UI state is already updated
      }

      // Show success message
      toast({
        description: t("All messages cleared"),
        duration: 2000
      });
    } catch (error) {
      console.error("[Chat] Error during clear operation:", error);
      
      // Even if an error occurs, attempt to reset critical state
      try {
        setModelMessages((prev) => ({
          ...prev,
          [selectedModel]: [createWelcomeMessage(selectedModel), createDisclaimerMessage()],
        }));
        setUserMessageCount(0);
        setHasSushiBeenShown(false);
        setShowSushiSplash(false);
      } catch (stateError) {
        console.error("[Chat] Failed to reset state after error:", stateError);
      }

      // Always show feedback to user
      toast({
        description: t("All messages cleared"),
        duration: 2000
      });
    }
  };

  const handleLogoClick = async () => {
    try {
      await triggerHaptic("success");
      setIsLogoRotating(true);

      // Clear local storage and session storage
      localStorage.clear();
      sessionStorage.clear();
      console.log("[MJ] Local Storage & Session Storage Cleared Successfully");

      // Show toast notification
      toast({
        description: t("All data cleared"),
        duration: 2000
      });

      // Force a hard reload of the page
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("[MJ] Error during logo click:", error);
      // Ensure page reload even if errors occur
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  };

  const handleModelChange = (model: ModelType) => {
    setSelectedModel(model);
    try {
      localStorage.setItem('selectedModel', model);
      console.log(`[Chat] Model changed to ${model}`);
    } catch (error) {
      console.warn('[Chat] Failed to save model selection to localStorage:', error);
      // The state is still updated in memory, so functionality continues
    }
  };

  const handleSushiClick = async () => {
    try {
      await triggerHaptic("selection");
      const newTab = window.open('https://www.buymeacoffee.com/mjallal', '_blank');
      if (newTab) {
        newTab.opener = null;
      }
    } catch (error) {
      console.warn("[MJ] Failed to handle sushi click:", error);
      const newTab = window.open('https://www.buymeacoffee.com/mjallal', '_blank');
      if (newTab) {
        newTab.opener = null;
      }
    }
  };

  const handleFrameworkChange = (framework: Framework) => {
    setSelectedFramework(framework);
    try {
      localStorage.setItem('selectedFramework', framework);
      console.log(`[Chat] Framework changed to ${framework}`);
    } catch (error) {
      console.warn('[Chat] Failed to save framework selection to localStorage:', error);
      // The state is still updated in memory, so functionality continues
    }
  };
  
  // This function handles UI-triggered locale changes using the language context's setLocale
  const updateLocale = async (locale: LocaleType) => {
    try {
      await triggerHaptic('selection');
      await handleLocaleChange(locale); // Use the context method instead of local state
      
      // Force refresh translations in the UI
      console.log(`[i18n] Language changed to ${locale}, refreshing UI...`);
      
      // Regenerate welcome and disclaimer messages with new translations
      setModelMessages(prev => {
        // Create fresh welcome and disclaimer messages for each model
        const updatedMessages: ModelMessages = {
          claude: [...prev.claude],
          deepseek: [...prev.deepseek],
          mjlab: [...prev.mjlab]
        };
        
        // Replace the first two messages (welcome and disclaimer) for each model
        Object.keys(updatedMessages).forEach(model => {
          const modelKey = model as ModelType;
          if (updatedMessages[modelKey].length >= 2) {
            // Find the welcome and disclaimer messages by their IDs
            const welcomeIndex = updatedMessages[modelKey].findIndex((msg: Message) => msg.id === 'welcome');
            const disclaimerIndex = updatedMessages[modelKey].findIndex((msg: Message) => msg.id === 'disclaimer');
            
            // Replace welcome message if found
            if (welcomeIndex !== -1) {
              updatedMessages[modelKey][welcomeIndex] = createWelcomeMessage(modelKey);
            }
            
            // Replace disclaimer message if found
            if (disclaimerIndex !== -1) {
              updatedMessages[modelKey][disclaimerIndex] = createDisclaimerMessage();
            }
          }
        });
        
        return updatedMessages;
      });
      
    } catch (error) {
      console.warn('[Chat] Language change failed:', error);
      toast({
        title: t("Error"),
        description: t("Failed to change language. Please try again."),
        variant: "destructive",
      });
    }
  };

  const toggleDrawer = async () => {
    try {
      await triggerHaptic('selection');
      setIsDrawerOpen(prev => !prev);
    } catch (error) {
      console.warn('[Chat] Haptic feedback failed:', error);
      setIsDrawerOpen(prev => !prev);
    }
  };

  // Use useMemo for derived message data to prevent recalculations on every render
  const currentMessages = useMemo(() => modelMessages[selectedModel], [modelMessages, selectedModel]);
  return (
    <TooltipProvider>
      <div className="chat-layout bg-background">
        {testError ? (
          <ErrorPage
            code={testError.code.toString()}
            title="Error Occurred"
            subtitle={testError.message}
          />
        ) : (
          <>
            <header className="chat-header">
              <div className="h-full px-4">
                <div className="flex items-center justify-between h-full max-w-4xl mx-auto">
                  <div
                    className="chat-header__branding flex items-center gap-2 sm:gap-3"
                    style={{ cursor: "pointer" }}
                    onClick={handleLogoClick}
                  >
                    <motion.img
                      src="/assets/ui/LOADER-MJALLAL.webp"
                      alt="MJ Logo"
                      className="chat-header__logo w-8 h-8 rounded-full object-cover sm:w-10 sm:h-10"
                      animate={{ rotate: isLogoRotating ? 360 : 0 }}
                      transition={{ duration: 1, ease: "easeInOut" }}
                    />
                    <div className="chat-header__title-group">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <h1 className="chat-header__title text-lg font-semibold text-foreground sm:text-2xl">
                          <Trans i18nKey="Leen">Leen</Trans>
                        </h1>
                        <span className="chat-header__badge px-1.5 py-0.5 text-xs font-semibold rounded-md bg-primary/10 text-primary border border-primary/20">
                          <Trans i18nKey="by MJ">by MJ</Trans>
                        </span>
                        {/* DevTools button - only visible in development */}
                        {/* DevToolsButton removed */}
                      </div>
                      <p className="chat-header__subtitle text-xs text-muted-foreground sm:text-sm">
                        <Trans i18nKey="Labor Law Expert">Labor Law Expert</Trans>
                      </p>
                    </div>
                  </div>
                  <div className="chat-header__actions flex items-center gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => window.open("https://mjallal.sa", "_self")}
                      className="chat-header__link px-2 gap-1.5 about-mj-btn"
                      aria-label="Visit mjallal.sa website"
                    >
                      <span className="!font-semibold">MJ</span>
                      <ExternalLink className="h-4 w-4 transition-colors duration-300" aria-hidden="true" />
                    </Button>
                    <ThemeToggle />
                    <CSATFeedback 
                      ref={csatFeedbackRef}
                      buttonVariant="icon" 
                    />
                  </div>
                </div>
              </div>
            </header>

            <main className="chat-main matrix-bg" ref={chatContainerRef}>
              <div className="max-w-4xl mx-auto px-4 py-4">
                <div className="chat-messages space-y-4 pb-10 md:pb-12 lg:pb-16 sm:pb-28">
                  <AnimatePresence initial={false}>
                    {currentMessages.map((message) => (
                      <motion.div
                        key={message.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{
                          duration: 0.4,
                          ease: [0.4, 0, 0.2, 1],
                          opacity: { duration: 0.3 },
                          y: { duration: 0.4 }
                        }}
                        className="chat-message"
                      >
                        <MessageBubbleWrapper message={message} model={selectedModel} />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            </main>

            <AnimatePresence>
              {showSushiSplash && (
                <motion.div
                  className="fixed inset-0 flex items-center justify-center pointer-events-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  style={{ zIndex: 100 }}
                >
                  <motion.div
                    initial={{
                      scale: 0.2,
                      opacity: 0
                    }}
                    animate={{
                      scale: 1,
                      opacity: 0.95,
                      y: [0, -5, 0],
                      transition: {
                        duration: 1,
                        scale: {
                          type: "spring",
                          stiffness: 200,
                          damping: 12,
                          mass: 1
                        },
                        opacity: {
                          duration: 0.4,
                          ease: "easeOut"
                        },
                        y: {
                          repeat: Infinity,
                          duration: 2,
                          ease: "easeInOut"
                        }
                      }
                    }}
                    whileInView={{
                      scale: [1, 1.05, 1],
                      transition: {
                        duration: 1.8,
                        repeat: Infinity,
                        ease: [0.4, 0, 0.2, 1]
                      }
                    }}
                    exit={{
                      scale: 0.2,
                      opacity: 0,
                      transition: {
                        duration: 0.5,
                        scale: {
                          type: "spring",
                          stiffness: 200,
                          damping: 15
                        },
                        opacity: {
                          duration: 0.3,
                          ease: "easeOut"
                        }
                      }
                    }}
                    className="text-7xl sm:text-8xl md:text-9xl select-none"
                    style={{
                      filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.1))",
                      transformOrigin: "center center"
                    }}
                  >
                    🍣
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            <footer className="chat-footer fixed bottom-0 left-0 right-0 border-t border-primary/10 bg-background/80 backdrop-blur-sm z-50">
              <div className="mx-auto max-w-4xl px-2 py-3 sm:px-4">
                {/* Additional controls can be added here if needed */}
                <div className="chat-footer__controls space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 w-full">
                      <div className="flex items-center gap-2 flex-1">
                        <div className="w-full flex gap-2">
                          {/* Desktop Sushi Button */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={handleSushiClick}
                                aria-label="Support"
                                className="hidden md:flex h-10 w-10 items-center justify-center hover:bg-primary/5"
                              >
                                <span className="text-lg">🍣</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-semibold"><Trans i18nKey="Support the project">Support the project</Trans></p>
                            </TooltipContent>
                          </Tooltip>
                          {/* Mobile Top Row Controls */}
                          <div className="md:hidden flex flex-1 items-center justify-between gap-2 w-full">
                            {/* Mobile Language Selector */}
                            <LanguageSelector
                              selectedLocale={selectedLocale}
                              onLocaleChange={updateLocale}
                              disabled={isPending}
                              className="h-10 flex-[1.5]"
                            />
                            
                            {/* Mobile Font Size Controls */}
                            <div className="w-[124px]">
                              <MemoizedFontSizeControls className="w-full" />
                            </div>
                            
                            {/* Mobile Clear History Button */}
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={handleClear}
                              aria-label="Clear chat history"
                              className="h-10 w-[48px] flex items-center justify-center hover:bg-primary/5"
                            >
                              <Trash2 className="h-5 w-5" />
                            </Button>
                            
                            {/* Mobile Toggle Button (Chevron) */}
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={toggleDrawer}
                              aria-label={isDrawerOpen ? "Close settings" : "Open settings"}
                              className="h-10 w-[48px] flex items-center justify-center hover:bg-primary/5"
                            >
                              {isDrawerOpen ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronUp className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          {/* Desktop Language Selector and Model/Framework Selectors */}
                          <div className="hidden md:flex flex-1 gap-2">
                            {/* Desktop Language Selector - placed before model selector */}
                            <LanguageSelector
                              selectedLocale={selectedLocale}
                              onLocaleChange={updateLocale}
                              disabled={isPending}
                              className="h-10"
                            />
                            
                            {/* Desktop Model Selector - Hidden for now */}
                            {/* Temporarily hidden per request, will be restored in future
                            <MemoizedModelSelector
                              selectedModel={selectedModel}
                              onModelChange={handleModelChange}
                              disabled={isPending}
                              showMjLab={showMjLab}
                              className="flex-1"
                            />
                            */}
                            
                            {/* Desktop Framework Selector - Now takes more space since model selector is hidden */}
                            <FrameworkSelector
                              selectedFramework={selectedFramework}
                              onFrameworkChange={handleFrameworkChange}
                              disabled={isPending}
                              className="flex-1 min-w-[240px]"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Desktop controls (right side) */}
                      <div className="hidden md:flex items-center gap-2">
                        <MemoizedFontSizeControls />
                        <Button
                          variant="outline"
                          onClick={handleClear}
                          aria-label="Clear chat history"
                          className="h-10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Controls Drawer */}
                  <AnimatePresence>
                    {isDrawerOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          duration: 0.15,
                          ease: [0.4, 0, 0.2, 1],
                          opacity: { duration: 0.1 }
                        }}
                        className="md:hidden border-t border-primary/10 pt-3"
                        style={{
                          willChange: "transform, opacity, height",
                          contain: "content"
                        }}
                      >
                        {/* Mobile controls grid with model and framework selectors */}
                        <div className="grid grid-cols-12 gap-2 px-3">
                          {/* Model Selector - 6 columns (1/2) - Hidden for now */}
                          {/* Temporarily hidden per request, will be restored in future
                          <div className="col-span-6">
                            <MemoizedModelSelector
                              selectedModel={selectedModel}
                              onModelChange={handleModelChange}
                              disabled={isPending}
                              showMjLab={showMjLab}
                              className="h-10 w-full"
                            />
                          </div>
                          */}

                          {/* Framework Selector - Now takes full width since model selector is hidden */}
                          <div className="col-span-12">
                            <FrameworkSelector
                              selectedFramework={selectedFramework}
                              onFrameworkChange={handleFrameworkChange}
                              disabled={isPending}
                              className="h-10 w-full"
                            />
                          </div>

                          {/* Sushi Button - Full width */}
                          <div className="col-span-12 mt-1">
                            <Button
                              variant="outline"
                              onClick={handleSushiClick}
                              aria-label="Support"
                              className="h-10 w-full flex items-center justify-center gap-2"
                            >
                              <span className="text-lg mr-1">🍣</span>
                              <span className="font-semibold"><Trans i18nKey="Support the project">Support the project</Trans></span>
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Chat Input */}
                  <div className="relative">
                    <MemoizedChatInput
                      onSend={handleSend}
                      disabled={isPending}
                      elapsedTime={elapsedTime}
                      isPending={isPending}
                    />
                  </div>
                </div>
              </div>
            </footer>
          </>
        )}
      </div>
    </TooltipProvider>
  );
};